/**
 * Wire protocol shared by server and client. JSON messages over a WebSocket.
 *
 * Every state change produces a full `room` snapshot for each connection,
 * tailored to that connection's seat (so hidden cards never leave the server).
 * Snapshots are small (a few KB) so we don't bother with diffs.
 */

import type { Seat } from './cards.js';
import type { GameAction } from './game.js';
import type { RulesConfig } from './rules.js';
import type { PlayerView } from './view.js';

export const PROTOCOL_VERSION = 1;

export const EMOTES = ['👍', '👏', '😂', '😮', '😬', '🔥', '🤝', '🙈'] as const;
export type Emote = (typeof EMOTES)[number];

export const LIMITS = {
  nameMax: 20,
  chatMax: 200,
  chatHistory: 60,
  maxSpectators: 12,
  maxMessageBytes: 4096,
  emailMax: 254,
  /** Digits in an emailed sign-in code. */
  codeLength: 6,
} as const;

/** The signed-in account, sent to its owner only. */
export interface AccountInfo {
  displayName: string;
  email: string;
  /** Who may see this player's profile. Older servers don't send it (treat as 'friends'). */
  profileVisibility?: ProfileVisibility;
  /** The game's admin (David): can reset a player's name. */
  admin?: boolean;
  /** From the birth year asked at sign-up. Null: not asked yet (made before Oct 22, 2026); treated as 13-17. */
  ageBand?: '13-17' | '18+' | null;
}

/**
 * Who may see a player's profile (decided with David, Oct 20, 2026): private unless the player allows it,
 * 'friends' by default, and 'everyone' is not offered to players under 18 (age bands arrive with the privacy phase).
 */
export type ProfileVisibility = 'private' | 'friends' | 'everyone';
export const PROFILE_VISIBILITIES: readonly ProfileVisibility[] = ['private', 'friends', 'everyone'];

/** A player's numbers for one kind of game (people-only, or bot games). */
export interface PlayerStats {
  games: number;
  wins: number;
  losses: number;
  handsPlayed: number;
  tricksWon: number;
  /** Times this player named trump (ordered up or called). */
  trumpCalls: number;
  callsRound1: number;
  callsRound2: number;
  /** Their call took 3+ tricks. */
  callsMade: number;
  /** Their call was euchred. */
  callsEuchred: number;
  /** Dealer forced to call in round 2 (stick the dealer on, everyone else passed). */
  stuckAsDealer: number;
  loners: number;
  lonersMade: number;
  /** Loners that took all 5 tricks (4 points). */
  lonerMarches: number;
  /** Hands this player's team took all 5 tricks as makers. */
  marches: number;
  /** Hands where this player's team defended and euchred the makers. */
  euchresScored: number;
  defendAloneTries: number;
  defendAloneWins: number;
  /** Games this player sat in that were cancelled before anyone won (not counted as wins or losses). */
  unfinished: number;
}

/** A player's record, kept apart for people-only games and bot games (a bot in a seat for more than two hands). */
export interface PlayerRecord {
  people: PlayerStats;
  bots: PlayerStats;
}

export const REPORT_REASONS = ['Offensive name', 'Offensive chat', 'Cheating or unfair play', 'Something else'] as const;

/** Between you and another signed-in player: none, you asked (out), they asked (in), or friends. */
export type FriendStatus = 'none' | 'pending_out' | 'pending_in' | 'friends';

/** Your friends screen: your own code to share, and everyone you're friends with or have a request with. */
export interface FriendsInfo {
  /** Share this, or the link /f/CODE; whoever enters it becomes your friend. */
  code: string;
  friends: { accountId: string; name: string; status: FriendStatus; online: boolean }[];
  /** People you've blocked (you can unblock them here). */
  blocked: { id: string; name: string }[];
}

/** What the server tells you about a profile you asked for. */
export interface ProfileInfo {
  playerId: string;
  name: string;
  /** Your own profile. */
  self: boolean;
  /** Signed in to an account (guests' stats stay on their device's player id). */
  signedIn: boolean;
  /** Whether you may see the stats. False: the record is left out. */
  visible: boolean;
  /** Only on your own profile, when signed in. */
  visibility?: ProfileVisibility;
  record?: PlayerRecord;
  /**
   * Skill rating from finished games with four people (starts at 1000). On your own profile, and on others' profiles
   * only when they chose "Everyone" (David, Oct 22, 2026).
   */
  rating?: { value: number; games: number; provisional: boolean };
  /** Both signed in and not yourself: where your friendship stands (so the screen can offer "Add friend"). */
  friend?: FriendStatus;
  /** Their account id, when friend is set (for the friend buttons). */
  accountId?: string;
}

/** Why a sign-in step failed, so the screen can say the right thing. */
export type AuthErrorCode =
  | 'invalid_email'
  | 'rate_limited'
  | 'bad_code'
  | 'expired'
  | 'too_many_tries'
  | 'unavailable'
  | 'name_taken'
  | 'name_not_allowed'
  | 'too_young'
  | 'bad_year';

export type ClientMessage =
  | { type: 'hello'; token?: string; name?: string; protocol?: number }
  | { type: 'set_name'; name: string }
  | { type: 'create_room'; rules?: Partial<RulesConfig> }
  | { type: 'join_room'; code: string; spectate?: boolean }
  | { type: 'leave_room' }
  | { type: 'sit'; seat: Seat }
  | { type: 'stand' }
  | { type: 'add_bot'; seat: Seat }
  | { type: 'remove_bot'; seat: Seat }
  | { type: 'set_ready'; ready: boolean }
  | { type: 'set_rules'; rules: Partial<RulesConfig> }
  | { type: 'start_game' }
  | { type: 'game_action'; action: GameAction }
  | { type: 'chat'; text: string }
  | { type: 'emote'; emote: Emote }
  | { type: 'new_game' }
  /** "I'm here": stop the stepped-away countdown, or take your seat back from the bot covering it. */
  | { type: 'im_here' }
  /** A tester's note. `context` is optional client detail (screen size, settings), kept short by the server. */
  | { type: 'feedback'; text: string; context?: Record<string, unknown> }
  /** Accounts: email me a sign-in code. */
  | { type: 'auth_request_code'; email: string }
  /** Accounts: here's the code from the email. Signs this browser in (creating the account the first time). */
  | { type: 'auth_verify_code'; email: string; code: string }
  /** Accounts: the birth year asked for a new account (only the age band, 13-17 or 18+, is kept). */
  | { type: 'auth_birth_year'; year: number }
  /** Accounts: sign this browser out; it goes back to being a guest with the same name. */
  | { type: 'auth_sign_out' }
  /** Profiles: show me a player's profile (mine when playerId is left out). */
  | { type: 'get_profile'; playerId?: string }
  /** Profiles: who may see my profile (signed-in players only). */
  | { type: 'set_profile_visibility'; visibility: ProfileVisibility }
  /** Friends (signed-in players): my code and list. */
  | { type: 'friends_get' }
  /** Friends: I entered a friend's code (or opened their /f/ link): we become friends. */
  | { type: 'friend_add_code'; code: string }
  /** Friends: ask someone at my table (they accept or decline). */
  | { type: 'friend_request'; playerId: string }
  | { type: 'friend_accept'; accountId: string }
  /** Remove a friend, decline a request, or cancel mine. */
  | { type: 'friend_remove'; accountId: string }
  /** Block someone: no friendship, no requests, no chat between you, and they can't join tables you host. */
  | { type: 'block'; playerId: string }
  /** Unblock, by the id from your blocked list. */
  | { type: 'unblock'; id: string }
  /** Report a player to the game's admin (reason is one of REPORT_REASONS; note is optional). */
  | { type: 'report'; playerId: string; reason: string; note?: string }
  /** Admin only: give a player with an unsuitable name a plain one ("Player 4821"). */
  | { type: 'admin_reset_name'; playerId: string }
  /** Everything kept about my account, as a file. */
  | { type: 'download_data' }
  /** Delete my account for good (confirm must be "DELETE"). */
  | { type: 'delete_account'; confirm: string }
  | { type: 'ping' };

export interface HumanOccupant {
  kind: 'human';
  playerId: string;
  name: string;
  connected: boolean;
  ready: boolean;
  /** Epoch ms when a bot will take over for a disconnected player (game in progress only). */
  botTakeoverAt: number | null;
  /** True while a bot is playing this seat on the human's behalf. */
  botControlled: boolean;
  /** Epoch ms when a bot will take over for a player who has gone quiet on their turn (the warning countdown). */
  idleTakeoverAt?: number | null;
  /** While a bot covers this seat: epoch ms until which the player can take it back. After that the bot keeps it. */
  returnBy?: number | null;
}

export interface BotOccupant {
  kind: 'bot';
  name: string;
}

export interface SeatInfo {
  seat: Seat;
  occupant: HumanOccupant | BotOccupant | null;
}

export interface ChatEntry {
  id: number;
  at: number;
  /** null for system messages. */
  from: { playerId: string; name: string; seat: Seat | null } | null;
  text: string;
  /** "vegas": the Vegas dealer heckling a bad play. Players on Plain dealer talk don't see these. */
  tone?: 'vegas';
  /** The same heckle in the Vegas After Dark voice (adults only), for players who picked it. Older clients ignore it. */
  afterDark?: string;
}

export interface RoomSnapshot {
  code: string;
  hostId: string;
  status: 'lobby' | 'playing' | 'finished';
  rules: RulesConfig;
  seats: SeatInfo[];
  spectators: { playerId: string; name: string; connected: boolean }[];
  you: { playerId: string; seat: Seat | null; isHost: boolean; spectator: boolean };
  game: PlayerView | null;
  chat: ChatEntry[];
  /** Server clock, so clients can render countdowns despite clock skew. */
  serverTime: number;
  /** Games completed in this room: [team0 wins, team1 wins]. */
  gamesWon: [number, number];
}

export type ServerMessage =
  | {
      type: 'welcome';
      token: string;
      playerId: string;
      name: string;
      protocol: number;
      /** The account this browser is signed in to, or null for a guest. Older clients ignore it. */
      account?: AccountInfo | null;
    }
  | { type: 'room'; room: RoomSnapshot }
  | { type: 'left_room' }
  | { type: 'error'; message: string; code?: string }
  | { type: 'chat'; entry: ChatEntry }
  | { type: 'emote'; seat: Seat | null; name: string; emote: Emote }
  | { type: 'pong' }
  /** Your feedback note was saved. */
  | { type: 'feedback_ok' }
  /** A sign-in code was emailed (sent whether or not an account exists yet). */
  | { type: 'auth_code_sent'; email: string }
  /** Signed in. `created`: this was a brand-new account. A fresh `welcome` follows with the account's name. */
  | { type: 'auth_ok'; account: AccountInfo; created: boolean }
  | { type: 'auth_error'; message: string; code: AuthErrorCode }
  /** The code was right and this is a new account: ask for the birth year before it's made. */
  | { type: 'auth_need_birth_year' }
  /** The profile you asked for. */
  | { type: 'profile'; profile: ProfileInfo }
  /** Your friends screen (sent when you ask, and again whenever it changes). */
  | { type: 'friends'; friends: FriendsInfo }
  /** "Download my data": the file's contents. */
  | { type: 'my_data'; data: Record<string, unknown> }
  /** Something worked; show this short note (e.g. "You and Dave are now friends."). */
  | { type: 'notice'; message: string };

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 5;

export function normalizeRoomCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, ROOM_CODE_LENGTH);
}
